import { makePage } from '@/components/page'
import { useVenueContext } from '@/services/venue'
import { useAppContext } from '@/pages/_app'
import tmpl from 'blueimp-tmpl'
import { HeadPanel } from '@/components/head-panel'
import { entries, keys } from 'shared/utils/array'
import { customDomains } from 'shared/custom-domains'

export default makePage(
  {
    clientRenderOnly: {},
    meta: {
      subTitle: 'terms:termsOfUse',
      noIndex: true,
    },
  },
  function TermsPage() {
    const { t } = useAppContext()
    const venueContext = useVenueContext()!

    const { venue, menuRoute } = venueContext

    const customDomainName = entries(customDomains).find(([, venueName]) => {
      return venueName === venue.name
    })?.[0]
    const venueDomain = customDomainName ? `https://${customDomainName}` : location.origin

    const venueUrl = customDomainName
      ? `https://${customDomainName}`
      : new URL(`/venue/${venue.name}`, location.origin).toString()

    const termsUrl = new URL(`/venue/${venue.name}/terms`, venueDomain).toString()

    const templateString = venue.features.termsAndConditionsHtml
    const templateVars = venue.features.termsAndConditionsVars ?? {}

    const collectingPersonalData =
      venue.features.vas?.feedback ||
      venue.features.vas?.bookTable ||
      keys(venue.features.orderingTypes ?? {}).length > 0

    if (!templateString && !collectingPersonalData) {
      return null
    }

    return (
      <div>
        <HeadPanel url={menuRoute.path} text={t('terms:termsOfUse')} />
        <div className="p-5 space-y-4">
          {templateString && (
            <div dangerouslySetInnerHTML={{ __html: tmpl(templateString, templateVars) }}></div>
          )}
          {collectingPersonalData && (
            <div dangerouslySetInnerHTML={{ __html: privacyPolicy({ venueUrl, termsUrl }) }}></div>
          )}
        </div>
      </div>
    )
  },
)

function privacyPolicy(params: { venueUrl: string; termsUrl: string }) {
  const { venueUrl, termsUrl } = params
  return `
<p><h1 class=\"text-lg font-medium mt-4\">ПОЛИТИКА В ОТНОШЕНИИ ОБРАБОТКИ ПЕРСОНАЛЬНЫХ ДАННЫХ</h1></p>

<p class=\"font-medium mt-2\">1. Основные понятия</p>
<p class=\"mt-2\">1.1. Оператор – ИП Гарифуллин Линар Ильдарович, ИНН 026406525764, ОГРНИП 324774600756940.</p>
<p class=\"mt-2\">1.2. Политика – настоящий документ, составленный Оператором в соответствии с
требованиями п. 2 ч. 1 ст. 18.1. Федерального закона от 27.07.2006 № 152-ФЗ
«О персональных данных» (далее – «152-ФЗ») в целях определения порядка Обработки
Персональных данных и мер по обеспечению безопасности Персональных данных
Субъектов Персональных данных, в том числе Пользователей.</p>
<p class=\"mt-2\">1.3. Персональные данные – любая информация, относящаяся прямо или косвенно к
определенному или определяемому физическому лицу.</p>
<p class=\"mt-2\">1.4. Обработка Персональных данных – любое действие (операция) или совокупность
действий (операций), совершаемых с использованием средств автоматизации или без
использования таких средств с Персональными данными, включая сбор, запись,
систематизацию, накопление, хранение, уточнение (обновление, изменение), извлечение,
использование, передачу (распространение, предоставление, доступ), обезличивание,
блокирование, удаление, уничтожение Персональных данных.</p>
<p class=\"mt-2\">1.5. Предоставление Персональных данных – действия, направленные на раскрытие
Персональных данных определенному лицу или определенному кругу лиц.</p>
<p class=\"mt-2\">1.6. Распространение Персональных данных – действия, направленные на раскрытие
Персональных данных неопределенному кругу лиц.</p>
<p class=\"mt-2\">1.7. Субъект Персональных данных – физическое лицо, обладающее прямо или косвенно определяющими его Персональными данными.</p>
<p class=\"mt-2\">1.8. Пользователь – Субъект Персональных данных, являющийся посетителем Сайта.</p>
<p class=\"mt-2\">1.9. Сайт – совокупность графических и информационных материалов, а также программ для ЭВМ и баз данных, обеспечивающих их доступность в информационно-
телекоммуникационной сети «Интернет» по адресу ${venueUrl}, предназначенных для осуществления Оператором его предпринимательской деятельности.</p>
<p class=\"mt-2\">1.10. Блокирование Персональных данных – временное прекращение Обработки
Персональных данных (за исключением случаев, если Обработка необходима для
уточнения Персональных данных).</p>
<p class=\"mt-2\">1.11. Обезличивание Персональных данных — действия, в результате которых невозможно определить без использования дополнительной информации принадлежность Персональных данных конкретному Пользователю или иному Субъекту Персональных данных.</p>
<p class=\"mt-2\">1.12. Уничтожение Персональных данных – любые действия, в результате которых
Персональные данные уничтожаются безвозвратно с невозможностью дальнейшего
восстановления содержания Персональных данных.</p>
<p class=\"mt-2\">1.13. Трансграничная передача Персональных данных – передача Персональных данных на территорию иностранного государства органу власти иностранного государства, иностранному физическому лицу или иностранному юридическому лицу.</p>

<p class=\"font-medium mt-2\">2. Общие положения</p>
<p class=\"mt-2\">2.1. Целью Политики является определение порядка Обработки Персональных данных и мер по обеспечению безопасности Персональных данных Субъектов Персональных данных, в том числе Пользователей.</p>
<p class=\"mt-2\">2.2. Оператор ставит своей важнейшей целью и условием осуществления своей деятельности соблюдение прав и свобод человека и гражданина при Обработке Персональных данных, в том числе защиту прав на неприкосновенность частной жизни, личную и семейную тайну.</p>
<p class=\"mt-2\">2.3. При организации Обработки Персональных данных Оператор руководствуется
следующими принципами:</p>
<p class=\"mt-2\">2.3.1. Обработка Персональных данных осуществляется на законной и справедливой основе.</p>
<p class=\"mt-2\">2.3.2. Обработка Персональных данных ограничивается достижением конкретных, заранее определенных и законных целей.</p>
<p class=\"mt-2\">2.3.3. Не допускается объединение баз данных, содержащих Персональные данные, Обработка которых осуществляется в целях, несовместимых между собой.</p>
<p class=\"mt-2\">2.3.4. Обработке подлежат только Персональные данные, отвечающие целям их Обработки.</p>
<p class=\"mt-2\">2.3.5. Содержание и объем обрабатываемых Персональных данных соответствуют заявленным целям Обработки.</p>
<p class=\"mt-2\">2.3.6. При Обработке Персональных данных обеспечивается точность Персональных данных, их достаточность и актуальность по отношению к целям Обработки Персональных данных.</p>
<p class=\"mt-2\">2.3.7. Хранение Персональных данных осуществляется в форме, позволяющей определить Субъекта Персональных данных, не дольше, чем этого требуют цели Обработки Персональных данных, если срок хранения Персональных данных не установлен 152-ФЗ; договором, стороной которого, выгодоприобретателем или поручителем по которому является Субъект Персональных данных, согласием Субъекта Персональных данных. Обрабатываемые Персональные данные подлежат Уничтожению либо Обезличиванию по достижении целей Обработки или в случае утраты необходимости в достижении этих целей.</p>
<p class=\"mt-2\">2.3.8. Обработка Персональных данных без согласия Субъекта Персональных данных не
допускается.</p>
<p class=\"mt-2\">2.4. Правовыми основаниями Обработки Персональных данных Оператором являются:
<br/>• Трудовой кодекс Российской Федерации от 30.12.2001 № 197-ФЗ;
<br/>• Гражданский кодекс Российской Федерации от 30.11.1994 № 51-ФЗ;
<br/>• Налоговый кодекс Российской Федерации от 31.07.1998 № 146-ФЗ;
<br/>• Семейный кодекс Российской Федерации от 29.12.1995 N 223-ФЗ;
<br/>• Федеральный закон от 01.04.1996 № 27-ФЗ «Об индивидуальном (персонифицированном) учете в системе обязательного пенсионного страхования»;
<br/>• Федеральный закон от 28.03.1998 г. № 53-ФЗ «О воинской обязанности и военной службе»;
<br/>• Федеральный закон от 19.05.1995 № 81-ФЗ (ред. от 08.06.2020) «О государственных
пособиях гражданам, имеющим детей»;
<br/>• Федеральный закон от 06.12.2011 № 402-ФЗ «О бухгалтерском учете»;
<br/>• Федеральный закон от 02.10.2007 № 229-ФЗ «Об исполнительном производстве»;
<br/>• Федеральный закон от 29.12.2006 № 255-ФЗ «Об обязательном социальном страховании
на случай временной нетрудоспособности и в связи с материнством»;
<br/>• Федеральный закон №426 от 28.12.2003 «О специальной оценке условий труда»;
<br/>• Трудовой договор, заключаемый между работником и Оператором;
<br/>• Договор гражданско-правового характера;
<br/>• Договор с индивидуальным предпринимателем;
<br/>• Согласие работника на Обработку Персональных данных;
<br/>• Согласие Пользователя на Обработку Персональных данных;
<br/>• Согласие родственника работника на Обработку Персональных данных;
<br/>• Поручение на Обработку Персональных данных от контрагентов Оператора.</p>
<p class=\"mt-2\">2.5. Оператор обеспечивает соответствие содержания и объема обрабатываемых
Персональных данных заявленным целям Обработки и, в случае необходимости,
принимает меры по устранению их избыточности по отношению к заявленным целям
Обработки.</p>
<p class=\"mt-2\">2.6. Обработка специальных категорий Персональных данных, касающихся расовой,
национальной принадлежности, политических взглядов, религиозных или философских
убеждений, интимной жизни, Оператором не осуществляется.</p>

<p class=\"font-medium mt-2\">3. Категории Субъектов Персональных данных. Цели Обработки Персональных
данных. Персональные данные, обрабатываемые Оператором</p>
<p class=\"mt-2\">3.1. Оператор производит Обработку Персональных данных следующих категорий Субъектов Персональных данных:</p>
<p class=\"mt-2\">3.1.1. Пользователи сайта Оператора ${venueUrl} (далее – «Пользователь», «Сайт»), если предоставляемых ими данных достаточно для прямой либо косвенной идентификации Субъекта Персональных данных.</p>
<p class=\"mt-2\">3.2. Для каждой категории Субъектов Персональных данных определены цели Обработки их Персональных данных:</p>
<p class=\"mt-2\">3.2.1. Обработка Персональных данных Пользователей производится в следующих целях:
<br/>• Улучшение работы Сайта, регистрация на Сайте.
<br/>• Участие в совершенствовании программ для ЭВМ, принадлежащих Оператору, и
оказываемых Оператором услуг.
<br/>• Установление обратной связи с Оператором и получение эффективной клиентской
технической поддержки, информации о программах для ЭВМ, порядке и условиях их
использования, услугах Оператора с использованием формы обратной связи,
размещенной на Сайте, а также по электронной почте, телефону и в мессенджерах.
<br/>• Совершение платежей в пользу Оператора и лиц, использующих программы для ЭВМ,
принадлежащие Оператору, в своей предпринимательской деятельности (рестораны,
кафе, другие заведения общественного питания и т.п.)
<br/>• Информирование о рекламных акциях, новостях и специальных предложениях
Оператора, участие в опросах и рекламных акциях Оператора, предоставление
таргетированной рекламы.
<br/>• Использование Сайта и программ для ЭВМ, принадлежащих Оператору.
<br/>• Оформление и оплата заказов с использованием сервиса Оператора ${venueUrl}.</p>
<p class=\"mt-2\">3.2.1.1. В состав обрабатываемых Персональных данных Пользователей входит:
<br/>• IP-адрес устройства;
<br/>• Данные об устройстве (тип, модель, название, ID);
<br/>• Данные об операционной системе (тип, версия);
<br/>• Данные о браузере (название, версия);
<br/>• Локация пользователя;
<br/>• Нажатия на элементы веб-страниц сайта;
<br/>• Источник захода на сайт;
<br/>• Сведения о просмотренных веб-страницах, заполнении полей ввода (за исключением
информации, непосредственно введенной в данные поля);
<br/>• Сведения о просмотре баннеров и видео;
<br/>• Данные, характеризующие аудиторные сегменты;
<br/>• Параметры сессии, данные о времени посещения;
<br/>• Идентификаторы пользователя, хранимые в файлах cookie, с использованием сервисов
Интернет-статистики (сервис Яндекс.Метрика);
<br/>• Номер телефона;
<br/>• Адрес электронной почты (e-mail);
<br/>• Фамилия, имя, отчество;
<br/>• Адрес доставки (включая название города, название улицы, номер дома, номер квартиры, код домофона, номер подъезда, номер этажа), указанный при оформлении заказа с использованием сервиса ${venueUrl};
<br/>• Состав корзины (заказа) в сервисе ${venueUrl} и комментарий к заказу.</p>
<p class=\"mt-2\">3.2.2. На Сайте происходит сбор и Обработка обезличенных данных о Пользователях (в том числе файлов «cookie») с помощью сервисов Интернет-статистики (сервис
Яндекс.Метрика).</p>

<p class=\"font-medium mt-2\">4. Права и обязанности Субъекта Персональных данных</p>
<p class=\"mt-2\">4.1. Субъект Персональных данных имеет право:
<br/>• принимать решение о предоставлении своих Персональных данных и давать согласие на их Обработку свободно, своей волей и в своем интересе;
<br/>• отозвать свое согласие на Обработку Персональных данных;
<br/>• получить у Оператора сведения, касающиеся Обработки его Персональных данных;
<br/>• требовать от Оператора уточнения своих Персональных данных, их блокирования или
уничтожения в случае, если они являются неполными, устаревшими, неточными,
незаконно полученными или не являются необходимыми для заявленных целей
Обработки, а также принимать предусмотренные законом меры по защите своих прав;
<br/>• потребовать ограничить Обработку его Персональных данных в целях маркетинговых
активностей;
<br/>• заявить возражение против решения, принятого исключительно на основании
автоматизированной Обработки Персональных данных;
<br/>• обжаловать действия или бездействие Оператора в уполномоченный орган по защите прав субъектов Персональных данных или в судебном порядке, если считает, что Оператор осуществляет Обработку его Персональных данных с нарушением требований 152-ФЗ или иным образом нарушает его права и свободы.</p>
<p class=\"mt-2\">4.2. Субъект Персональных данных обязан:
<br/>• сообщать достоверную информацию о себе и предоставлять документы, содержащие
Персональные данные, состав которых установлен законодательством Российской
Федерации в объеме, необходимом для цели Обработки;
<br/>• сообщать Оператору об уточнении (обновлении, изменении) своих Персональных данных.</p>
<p class=\"mt-2\">4.3. Оператор обязан:
<br/>• не раскрывать третьим лицам и не распространять Персональные данные без согласия
Субъекта Персональных данных, если иное не предусмотрено законодательством
Российской Федерации;
<br/>• предоставить безвозмездно, в доступной форме, без содержания Персональных данных
третьих лиц, за исключением случаев, если имеются законные основания для раскрытия
таких Персональных данных, Субъекту Персональных данных или его представителю
возможность ознакомления с Персональными данными с учетом ограничений,
установленных в 152-ФЗ;
<br/>• немедленно прекратить по требованию Субъекта Персональных данных Обработку
Персональных данных;
<br/>• разъяснить Субъекту Персональных данных юридические последствия отказа
предоставить Персональные данные, если предоставление Персональных данных
является обязательным в соответствии с законодательством Российской Федерации;
<br/>• разъяснить Субъекту Персональных данных порядок принятия решения на основании
исключительно автоматизированной Обработки его Персональных данных и возможные
юридические последствия такого решения, предоставить возможность заявить возражение против такого решения;
<br/>• если Персональные данные получены не от Субъекта Персональных данных, за
исключением случаев, предусмотренных 152-ФЗ, до начала Обработки таких
Персональных данных предоставить Субъекту Персональных данных следующую
информацию:
<br/>• наименование и адрес Оператора;
<br/>• цель Обработки Персональных данных и ее правовое основание;
<br/>• предполагаемые пользователи Персональных данных;
<br/>• права Субъекта Персональных данных;
<br/>• источник получения Персональных данных;
<br/>• обеспечить запись, систематизацию, накопление, хранение, уточнение (обновление,
изменение), извлечение Персональных данных граждан Российской Федерации с
использованием баз данных, находящихся на территории Российской Федерации, за
исключением случаев, указанных в 152-ФЗ;
<br/>• принимать меры, направленные на обеспечение выполнения обязанностей,
предусмотренных 152-ФЗ;
<br/>• принимать необходимые правовые, организационные и технические меры для защиты
Персональных данных от неправомерного или случайного доступа к ним, уничтожения,
изменения, блокирования, копирования, предоставления, распространения Персональных
данных, а также от иных неправомерных действий в отношении Персональных данных;
<br/>• сообщать в уполномоченный орган по защите прав субъектов Персональных данных по
запросу этого органа необходимую информацию в течение тридцати дней с даты
получения такого запроса;
<br/>• осуществлять Обработку запросов Субъектов Персональных данных в соответствии с
порядком, установленным в п. 5.4 Политики;
<br/>• устранять нарушения законодательства, допущенные при Обработке Персональных
данных, по уточнению, блокированию и уничтожению Персональных данных.</p>
<p class=\"mt-2\">4.4. Оператор имеет право:
<br/>• в случае отзыва Субъектом Персональных данных согласия на Обработку Персональных данных продолжить их Обработку без согласия Субъекта Персональных данных при наличии правовых оснований, установленных законодательством Российской Федерации;
<br/>• получить Персональные данные от лица, не являющегося Субъектом Персональных
данных, при условии предоставления подтверждения наличия правовых оснований,
установленных законодательством Российской Федерации.</p>

<p class=\"font-medium mt-2\">5. Порядок и условия Обработки Персональных данных</p>
<p class=\"mt-2\">Обработка Персональных данных Оператором осуществляется как с использованием, так и без использования средств автоматизации. Совокупность операций Обработки включает сбор, запись, систематизацию, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передачу (распространение, предоставление, доступ), блокирование, удаление, уничтожение Персональных данных.</p>
<p class=\"mt-2\">5.1. Хранение Персональных данных</p>
<p class=\"mt-2\">5.1.1. Для хранения Персональных применяются облачные решения сторонних компаний при обязательном соблюдении условий конфиденциальности Персональных данных и безопасности при их Обработке, установленных в договоре с подрядчиком.</p>
<p class=\"mt-2\">5.1.2. Доступ к Персональным данным в информационных системах
Персональных данных защищается встроенной системой аутентификации.</p>
<p class=\"mt-2\">5.1.3. Прекращение Обработки Персональных данных осуществляется по истечению
установленных сроков Обработки Персональных данных, при отзыве согласия субъекта
Персональных данных на Обработку его Персональных данных, а также при выявлении
неправомерной Обработки Персональных данных.</p>
<p class=\"mt-2\">5.1.4. Срок обработки персональных данных пользователей Сайта составляет до 10 лет с момента первого посещения пользователем Сайта, если иное не предусмотрено согласием Пользователя на Обработку Персональных данных.</p>

<p class=\"font-medium mt-2\">6. Условия передачи Персональных данных</p>
<p class=\"mt-2\">6.1. Организатор вправе поручить Обработку Персональных субъекта Персональных третьим лицам с согласия субъекта Персональных данных (когда данное согласие необходимо в соответствии с требованиями законодательства Российской Федерации и на основании заключаемого с этими лицами договора).</p>
<p class=\"mt-2\">6.2. Лица, осуществляющие Обработку Персональных данных в соответствии с заключаемым с Оператором договором, обязуются соблюдать принципы и правила Обработки и защиты Персональных данных, предусмотренные 152-ФЗ.</p>
<p class=\"mt-2\">6.3. В случае трансграничной передачи Персональных данных в страны, не обеспечивающие защиту прав Субъектов Персональных данных, за исключением случаев, предусмотренных законодательством Российской Федерации, трансграничная передача осуществляется на основании отдельного согласия в письменной форме.</p>

<p class=\"font-medium mt-2\">7. Меры защиты Персональных данных, принимаемые Оператором</p>
<p class=\"mt-2\">7.1. Оператор принимает необходимые и достаточные организационные и технические меры для защиты Персональных данных Субъектов Персональных данных от неправомерного или случайного доступа к ним, уничтожения, изменения, блокирования, копирования, распространения, а также от иных неправомерных действий.</p>
<p class=\"mt-2\">7.2. Меры по обеспечению безопасности Персональных данных, применяемые Оператором:
<br/>• назначение лица, ответственного за организацию Обработки Персональных данных;
<br/>• назначение лица, ответственного за обеспечение безопасности Персональных данных в
информационной системе Персональных данных;
<br/>• определение перечня работников, имеющих доступ к Персональным данным;
<br/>• разработка и утверждение организационно-распорядительных документов, определяющих порядок Обработки Персональных данных;
<br/>• организация порядка уничтожения Персональных данных по истечению срока их
Обработки;
<br/>• проведение внутренних проверок за соблюдением требований по обеспечению
безопасности Персональных данных;
<br/>• повышение уровня осведомленности работников о требованиях по обеспечению
безопасности Персональных данных;
<br/>• проведение оценки вреда, который может быть причинен субъектам Персональных данных в случае нарушения 152-ФЗ, соотношение указанного вреда и принимаемых мер,
направленных на обеспечение выполнения обязанностей, предусмотренных 152-ФЗ;
<br/>• обеспечение антивирусной защиты информационных систем Персональных данных;
<br/>• обновление программного обеспечения на регулярной основе;
<br/>• защита сетевой инфраструктуры (разграничение доступа, межсетевое экранирование);
<br/>• обеспечение отказоустойчивости и резервного копирования;
<br/>• определение правил доступа к Персональным данным, обрабатываемых в информационных системах Персональных данных.</p>

<p class=\"font-medium mt-2\">8. Условия Обработки Персональных данных</p>
<p class=\"mt-2\">8.1. Безопасность Персональных данных, которые обрабатываются Оператором,
обеспечивается путем реализации правовых, организационных и технических мер, в том
числе указанных в статье 7 Политики, необходимых для защиты Персональных данных от
несанкционированного, случайного или незаконного уничтожения, потери, изменения,
недобросовестного использования, раскрытия или доступа, а также иных незаконных форм Обработки в соответствии с требованиями действующего законодательства Российской Федерации в области защиты Персональных данных.</p>
<p class=\"mt-2\">8.2. Оператор обеспечивает сохранность Персональных данных и принимает все возможные меры, исключающие доступ к Персональным данным неуполномоченных лиц. Доступ к Персональным данным предоставляется только тем сотрудникам Оператора, которые нуждаются в этом для выполнения своих задач. Для защиты и обеспечения
конфиденциальности данных все сотрудники должны соблюдать внутренние правила и
процедуры в отношении Обработки Персональных данных. Они также должны следовать
всем техническим и организационным мерам безопасности, действующим для защиты
Персональных данных.</p>
<p class=\"mt-2\">8.3. Персональные данные Субъекта Персональных данных никогда, ни при каких условиях не будут переданы третьим лицам, за исключением случаев, связанных с исполнением действующего законодательства Российской Федерации, а также в случае, если Субъект Персональных данных выразил согласие на передачу Персональных данных третьим лицам.</p>
<p class=\"mt-2\">8.4. В случае выявления неточностей в составе Персональных данных Пользователь может актуализировать их самостоятельно посредством направления Оператору уведомления на адрес электронной почты Оператора support@foodeon.com с пометкой «Актуализация Персональных данных».</p>
<p class=\"mt-2\">8.5. Пользователь может в любой момент отозвать свое согласие на Обработку Персональных данных, направив Оператору уведомление посредством
электронной почты на электронный адрес Оператора support@foodeon.com с пометкой
«Отзыв согласия на Обработку Персональных данных».</p>
<p class=\"mt-2\">8.6. Оператор осуществляет запись, систематизацию, накопление, хранение, уточнение
(обновление, изменение), извлечение, распространение Персональных данных с
использованием баз данных, находящихся на территории Российской Федерации.</p>

<p class=\"font-medium mt-2\">9. Заключительные положения</p>
<p class=\"mt-2\">9.1. Все отношения Субъекта Персональных данных и Оператора, касающиеся Обработки
Персональных данных, не получившие отражения в Политике, регулируются в
соответствии с нормами законодательства Российской Федерации.</p>
<p class=\"mt-2\">9.2. Субъект Персональных данных может получить любые разъяснения по интересующим вопросам, касающимся Обработки его Персональных данных, обратившись к Оператору с помощью электронной почты support@foodeon.com с пометкой «Разъяснения относительно Обработки Персональных данных».</p>
<p class=\"mt-2\">9.3. Оператор имеет право в одностороннем порядке вносить изменения в Политику.
Новая редакция Политики вступает в силу с момента ее размещения на Сайте, если иное
не предусмотрено новой редакцией Политики. Действующая редакция Политики доступна
на Сайте по адресу: ${termsUrl}.</p>







<p><h1 class=\"text-lg font-medium mt-4\">СОГЛАСИЕ ПОСЕТИТЕЛЯ САЙТА ${venueUrl} НА ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ</h1></p>

<p class=\"mt-2\">Я (далее – «Субъект персональных данных»), в соответствии со статьей 9 Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных», свободно, своей волей и в своем интересе даю согласие ИП Гарифуллину Линару Ильдаровичу (ИНН 026406525764, ОГРНИП 324774600756940, далее – «Оператор»), на обработку своих персональных данных:</p>

<p class=\"font-medium mt-2\">Цель предоставления согласия на обработку персональных данных:</p>
<p class=\"mt-2\">1. Улучшение работы сайта ${venueUrl} (далее – «Сайт»), регистрация на Сайте.</p>
<p class=\"mt-2\">2. Участие в совершенствовании программ для ЭВМ, принадлежащих Оператору, и оказываемых Оператором услуг.</p>
<p class=\"mt-2\">3. Установление обратной связи с Оператором и получение эффективной клиентской технической поддержки, информации о программах для ЭВМ, порядке и условиях их использования, услугах Оператора с использованием формы обратной связи,
размещенной на Сайте, а также по электронной почте, телефону и в мессенджерах.</p>
<p class=\"mt-2\">4. Совершения платежей в пользу лиц, использующих программы для ЭВМ, принадлежащие Оператору, в своей предпринимательской деятельности (рестораны, кафе, иные заведения общественного питания и т. п.).</p>
<p class=\"mt-2\">5. Информирование Субъекта персональных данных о рекламных акциях, новостях и специальных предложениях Оператора/Третьих лиц, участие в опросах и рекламных акциях Оператора/Третьих лиц, предоставление таргетированной рекламы.</p>
<p class=\"mt-2\">6. Использование Сайта и программ для ЭВМ, принадлежащих Оператору, иных сервисов Оператора/Третьих лиц.</p>
<p class=\"mt-2\">7. Оформление и оплата заказов с использованием сервиса ${venueUrl}.</p>
<p class=\"mt-2\">8. Заключение и исполнение Оператором гражданско-правовых договоров с третьими лицами.</p>
<p class=\"mt-2\">9. Заключение Субъектом персональных данных договоров с Третьими лицами, в том числе предметом которых является предоставление Третьим лицом Субъекту персональных данных сервиса Третьего лица.</p>
<p class=\"mt-2\">10. Участие в комплексе маркетинговых мероприятий Оператора/Третьих лиц.</p>
<p class=\"mt-2\">11. Регистрация Субъекта персональных данных на страницах в информационно-телекоммуникационной сети «Интернет», принадлежащих Оператору/Третьим лицам.</p>
<p class=\"mt-2\">11. Передача (предоставление, доступ) Оператором персональных данных Субъекта персональных данных Третьим лицам.</p>

<p class=\"font-medium mt-2\">Персональных данные, на обработку которых дано согласие:</p>
<p class=\"mt-2\">• IP-адрес устройства;
<br/>• Данные об устройстве (тип, модель, название, ID);
<br/>• Данные об операционной системе (тип, версия);
<br/>• Данные о браузере (название, версия);
<br/>• Локация пользователя;
<br/>• Нажатия на элементы веб-страниц сайта;
<br/>• Источник захода на сайт;
<br/>• Сведения о просмотренных веб-страницах, заполнении полей ввода (за исключением информации, непосредственно введенной в данные поля);
<br/>• Сведения о просмотре баннеров и видео;
<br/>• Данные, характеризующие аудиторные сегменты;
<br/>• Параметры сессии, данные о времени посещения;
<br/>• Идентификаторы пользователя, хранимые в файлах cookie, с использованием сервисов Интернет-статистики (сервис Яндекс.Метрика);
<br/>• Номер телефона;
<br/>• Адрес электронной почты (e-mail);
<br/>• Фамилия, имя, отчество;
<br/>• Контактные данные в программах для обмена сообщениями;
<br/>• Адрес доставки (включая название города, название улицы, номер дома, номер квартиры, код домофона, номер подъезда, номер этажа), указанный при оформлении заказа с использованием сервиса ${venueUrl};
<br/>• Состав корзины в сервисе ${venueUrl} и комментарий к заказу.</p>

<p class=\"font-medium mt-2\">Способы и объем обработки персональных данных</p>
<p class=\"mt-2\">Способы обработки персональных данных, на обработку которых дано настоящее согласие: 
<br/>автоматизированная, а также без использования средств автоматизации
(неавтоматизированная), обработка персональных данных.</p>

<p class=\"mt-2\">Объем обработки персональных данных, на обработку которых дано настоящее согласие:
<br/>сбор, запись, систематизация, хранение, накопление, уточнение (обновление, изменение), извлечение, использование, блокирование, обезличивание, уничтожение, передача (распространение, предоставление, доступ) Третьим лицам.</p>

<p class=\"mt-2\">Под Третьими лицами понимаются любые контрагенты Оператора, в том числе использующие программы для ЭВМ, принадлежащие Оператору (рестораны, кафе, иные заведения общественного питания и т. п.), для оформления и оплаты заказов, а также для получения сведений, необходимых для связи с лицами, оформившими заказ.</p>

<p class=\"font-medium mt-2\">Согласие</p>
<p class=\"mt-2\">Я соглашаюсь и проинформирован/-а о том, что указанные выше персональные данные для описанных выше целей могут быть получены Оператором как непосредственно от меня, так и от третьих лиц. </p>
<p class=\"mt-2\">Настоящее согласие действует в течение 10 (десяти) лет.</p>
<p class=\"mt-2\">Настоящее согласие может быть отозвано мной в любой момент путем направления требования в адрес Оператора по адресу электронной почты support@foodeon.com. В таком случае действие настоящего согласия прекращается с момента поступления Оператору требования о прекращении обработки персональных данных.</p>

`
}

export {
  getVenueStaticPaths as getStaticPaths,
  getVenueStaticProps as getStaticProps,
} from '@/services/venue'
